// ═══════════════════════════════════════════════════════════════
// API ROUTES — the browser finds the right edge function by the PATH
// ═══════════════════════════════════════════════════════════════
// MOS's backend is more than one Supabase edge function since 2026-10-23: the
// money family (/money, /qbo, /ar, /monday/bid-sales) lives in `mos-money`,
// everything else in `make-server-e3a97ae5`. Which path lives where is ONE
// rule, shared with the server: supabase/functions/make-server-e3a97ae5/
// _shared/function-homes.ts.
//
// The frontend builds its URLs in ~270 places as
//   `https://${projectId}.supabase.co/functions/v1/make-server-e3a97ae5`
// and none of them changed. Instead installApiRouter(), called first thing in
// main.tsx, wraps window.fetch: a request to a make-server-e3a97ae5 address
// whose path has another home is rewritten to that home before it leaves the
// browser. The PATH decides, never the screen — so a /jobs call made from a
// Books screen still reaches the main function, and a /money call made from
// a job page reaches mos-money.
//
// A link that is not a fetch (an <a href>, window.open) cannot go through the
// wrapper: build it with apiUrl(path) instead. Both read the same rule.
//
// Why a router and not an edit of the 270 lines: an old bundle keeps working
// against the main function's forwarder either way; what the router buys is
// that moving the NEXT family touches one table, not a sweep of src/ that
// would miss one and 404 a screen for the one person who opens it.
// ═══════════════════════════════════════════════════════════════

import { projectId } from './supabase/info';
import { MAIN_FUNCTION, functionFor } from '../../supabase/functions/make-server-e3a97ae5/_shared/function-homes.ts';

const FUNCTIONS_ORIGIN = `https://${projectId}.supabase.co/functions/v1/`;
const MAIN_HEAD = `${FUNCTIONS_ORIGIN}${MAIN_FUNCTION}`;

/** The full URL of a route path (`/money/bank/tie-out`), on the function that
 *  serves it. For links and window.open; a fetch needs nothing, see below. */
export function apiUrl(path: string): string {
  return `${FUNCTIONS_ORIGIN}${functionFor(path)}${path}`;
}

/** A make-server-e3a97ae5 URL rewritten onto the function that serves its
 *  path; any other URL comes back as it was. Pure, so the goldens can prove it. */
export function routeApiUrl(url: string): string {
  if (!url.startsWith(MAIN_HEAD)) return url;
  const path = url.slice(MAIN_HEAD.length);
  if (path !== '' && !path.startsWith('/') && !path.startsWith('?')) return url; // make-server-e3a97ae5-something
  const fn = functionFor(path || '/');
  return fn === MAIN_FUNCTION ? url : `${FUNCTIONS_ORIGIN}${fn}${path}`;
}

/** The URL a fetch() argument names: a string, a URL, or a Request. */
function urlOf(input: RequestInfo | URL): string | null {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
  return null;
}

let installed = false;

/** Wrap window.fetch once so every existing call finds its function. Idempotent;
 *  main.tsx calls it before anything else runs. */
export function installApiRouter(): void {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;
  const realFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const url = urlOf(input);
    if (url === null) return realFetch(input as RequestInfo, init);
    const routed = routeApiUrl(url);
    if (routed === url) return realFetch(input as RequestInfo, init);
    // A Request carries its own method, headers and body: keep them, change the URL.
    if (typeof Request !== 'undefined' && input instanceof Request) return realFetch(new Request(routed, input), init);
    return realFetch(routed, init);
  }) as typeof window.fetch;
}
